/**
 * Whether the chip surfaces show the Hismaic reference form under the transliteration.
 *
 * One shared flag rather than a per-component checkbox, because the chip strip and the box
 * inspector's class picker sit in the same rail: a toggle that compacted one and left the other
 * tall would read as a bug. The record page reads it too, so the preference follows the person
 * rather than the screen.
 *
 * **On by default.** The forms are the reason the palette can be read without knowing the
 * alphabet, and a feature nobody discovers helps nobody; it is off in one click for anyone who
 * has learned the letters and wants the density back. Per browser rather than per account, on
 * the same reasoning as the palette key in `TokenStrip` and the sidebar in `nav.svelte.ts`: it
 * tracks how familiar this person is with the script, which is a property of the person.
 */

const STORAGE_KEY = 'hismaic.palette.glyphForms';

function loadShow(): boolean {
  if (typeof localStorage === 'undefined') return true;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return true;
    return raw !== 'off';
  } catch {
    return true;
  }
}

function storeShow(show: boolean): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, show ? 'on' : 'off');
  } catch {
    // A browser with storage disabled gets a working toggle, just a forgetful one.
  }
}

class GlyphPref {
  show = $state(loadShow());

  set(show: boolean): void {
    this.show = show;
    storeShow(show);
  }

  toggle(): void {
    this.set(!this.show);
  }

  /** Re-read from storage. Tests use it to start from a known state. */
  reload(): void {
    this.show = loadShow();
  }
}

export const glyphPref = new GlyphPref();
